'use client'

import React, { ReactNode, MouseEvent, useState } from 'react'
import Link from 'next/link'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  ChevronDownIcon,
  ChevronRightIcon,
  FolderIcon,
  MoreHorizontalIcon,
  EyeIcon,
  EditIcon,
  Trash2Icon,
  ListTreeIcon,
  SquarePlusIcon,
  CheckIcon,
  XIcon
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { LoadingState, DeleteConfirmationDialog } from '@/components/partials'
import { useCategoriesTree } from '@/hooks'
import { CategoryType } from '@/types/category'
import { cn } from '@/lib/utils'

// Recursive component to render a category and its children
const CategoryItem = ({
  category,
  level = 0,
  onView,
  onEdit,
  onDelete,
  renderActionItems
}: {
  category: CategoryType
  level?: number
  onView: (id: string) => void
  onEdit: (id: string) => void
  onDelete: (id: string) => void
  renderActionItems: (id: string, isDropdown: boolean) => ReactNode
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const t = useTranslations()

  // Get the current locale's translation or fallback to the first available
  const getLocalizedName = (category: CategoryType) => {
    // Try to get the current locale's translation
    const currentLocale = typeof window !== 'undefined' ? document.documentElement.lang || 'en-US' : 'en-US'

    if (category.translations[currentLocale]) {
      return category.translations[currentLocale].name
    }

    // Fallback to the first available translation
    const firstLocale = Object.keys(category.translations)[0]
    return category.translations[firstLocale]?.name || 'Unnamed Category'
  }

  const hasChildren = category.children && category.children.length > 0

  const handleExpandToggle = (e: MouseEvent) => {
    e.stopPropagation()
    if (hasChildren) {
      setIsExpanded(!isExpanded)
    }
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div className="category-item">
          <div
            className={cn(
              'hover:bg-muted/50 flex cursor-pointer items-center rounded p-2',
              isExpanded && 'bg-muted/30'
            )}
            style={{ paddingLeft: `${level * 20 + 8}px` }}>
            {/* Expand/collapse button */}
            {hasChildren ? (
              <Button variant="ghost" size="icon" className="mr-1 h-5 w-5 p-0" onClick={handleExpandToggle}>
                {isExpanded ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
              </Button>
            ) : (
              <div className="w-6"></div> // Spacer for alignment
            )}

            {/* Category info */}
            <div className="flex grow items-center gap-2" onClick={handleExpandToggle}>
              <FolderIcon className="text-muted-foreground h-4 w-4" />
              <span className="ml-2">
                {category.active ? (
                  <CheckIcon className="mx-auto h-5 w-5 text-green-500" />
                ) : (
                  <XIcon className="mx-auto h-5 w-5 text-red-500" />
                )}
              </span>
              <span>{getLocalizedName(category)}</span>
              <span className="text-muted-foreground ml-2 hidden text-xs lg:flex">ID: {category.id}</span>
            </div>

            {/* Dropdown menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto h-7 w-7 p-0"
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}>
                  <span className="sr-only">{t('open_menu')}</span>
                  <MoreHorizontalIcon className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">{renderActionItems(category.id, true)}</DropdownMenuContent>
            </DropdownMenu>
          </div>

          {isExpanded && hasChildren && (
            <div className="category-children">
              {category.children?.map(child => (
                <CategoryItem
                  key={child.id}
                  category={child}
                  level={level + 1}
                  onView={onView}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  renderActionItems={renderActionItems}
                />
              ))}
            </div>
          )}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>{renderActionItems(category.id, false)}</ContextMenuContent>
    </ContextMenu>
  )
}

const Tree = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const [deleteCategoryId, setDeleteCategoryId] = useState<string | null>(null)
  const { data: categories, isLoading, error, refetch } = useCategoriesTree()

  const handleView = (id: string) => {
    router.push(`/products/categories/${id}`)
  }

  const handleEdit = (id: string) => {
    router.push(`/products/categories/${id}/edit`)
  }

  const handleDelete = (id: string) => {
    setDeleteCategoryId(id)
  }

  const confirmDeleteCategory = async () => {
    if (deleteCategoryId) {
      // Here you would call the delete API
      // For now, we'll just show a success property
      toast.success(t('deleted_successfully'))
      setDeleteCategoryId(null)

      // Refresh the list
      await queryClient.invalidateQueries({ queryKey: ['categories-tree'] })
    }
  }

  const renderActionItems = (id: string, isDropdown = true) => {
    const ItemComponent = isDropdown ? DropdownMenuItem : ContextMenuItem

    return (
      <>
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            handleView(id)
          }}>
          <EyeIcon className="h-4 w-4" />
          {t('view')}
        </ItemComponent>
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            handleEdit(id)
          }}>
          <EditIcon className="h-4 w-4" />
          {t('edit')}
        </ItemComponent>
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            handleDelete(id)
          }}
          className="text-destructive focus:text-destructive">
          <Trash2Icon className="h-4 w-4" />
          {t('delete')}
        </ItemComponent>
      </>
    )
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ListTreeIcon className="h-5 w-5" />
                {t('categories')}
              </CardTitle>
              <CardDescription>{t('categories_description')}</CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <Button asChild>
                <Link href="/products/categories/create">
                  <SquarePlusIcon />
                  <span className="hidden flex-row items-center justify-items-center lg:flex">{t('create')}</span>
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link href="/products/categories">
                  <FolderIcon />
                  <span className="hidden flex-row items-center justify-items-center lg:flex">{t('list_view')}</span>
                </Link>
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch}>
            {categories && (
              <div className="space-y-1 rounded-md border p-2">
                {categories.length === 0 ? (
                  <div className="text-muted-foreground p-4 text-center">{t('no_results')}</div>
                ) : (
                  categories.map(category => (
                    <CategoryItem
                      key={category.id}
                      category={category}
                      onView={handleView}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      renderActionItems={renderActionItems}
                    />
                  ))
                )}
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      <DeleteConfirmationDialog
        open={!!deleteCategoryId}
        onCancel={() => setDeleteCategoryId(null)}
        onConfirm={confirmDeleteCategory}
        isDeleting={false}
      />
    </>
  )
}

export default Tree
